if (!customElements.get("m-recently-viewed")) {
  class MRecentlyViewed extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        slideControls: ".m-slider-controls",
        slideContainer: ".m-mixed-layout__wrapper", // slideContainer
      };
      this.domNodes = queryDomNodes(this.selectors, this);

      this.enableSlider = this.dataset.enableSlider === "true";
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.items = this.dataset.productsPerRow;
      this.mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      this.productsToshow = parseInt(this.dataset.productsToShow);

      // A fresh visitor has no history: never request an unfiltered search page.
      this.query = this.getQueryString();
      if (!this.query) return;
      if ('IntersectionObserver' in window) {
        let started = false;
        const start = () => {
          if (started || !this.isConnected) return;
          started = true;
          this.observer.disconnect();
          this.init();
        };
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) start();
        }, { rootMargin: '600px' });
        // The custom element starts display:none, so observe its section boundary.
        this.observer.observe(this.closest('.shopify-section') || this.parentElement);
        // Also start once the page has loaded and is idle. Rendering only when the shopper reached the
        // bottom inserted the whole rail (~490 px) above the footer while it was on screen (phone CLS
        // 0.15 on perfume pages, Oct 2026); after load the shopper is usually still above it.
        const whenIdle = () => ('requestIdleCallback' in window ? requestIdleCallback(start, { timeout: 2000 }) : setTimeout(start, 200));
        if (document.readyState === 'complete') whenIdle();
        else window.addEventListener('load', whenIdle, { once: true });
      } else this.init();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.onBreakpointChange) {
        document.removeEventListener("matchMobile", this.onBreakpointChange);
        document.removeEventListener("unmatchMobile", this.onBreakpointChange);
        this.onBreakpointChange = null;
      }
    }

    init() {
      fetch(this.dataset.url + encodeURIComponent(this.query))
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const recommendations = html.querySelector("m-recently-viewed");
          if (!recommendations) return;
          this.products = recommendations.querySelectorAll(".m-product-card").length;
          if (this.products > 0) {
            this.classList.remove("m:hidden");
          }
          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
            this.initByScreenSize();
            if (!this.onBreakpointChange) {
              this.onBreakpointChange = () => this.initByScreenSize();
              document.addEventListener("matchMobile", this.onBreakpointChange);
              document.addEventListener("unmatchMobile", this.onBreakpointChange);
            }

            MinimogTheme.CompareProduct?.setCompareButtonsState();
            MinimogTheme.Wishlist?.setWishlistButtonsState();
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }

    initByScreenSize() {
      if (!this.enableSlider) return;
      const { slideContainer, slideControls } = queryDomNodes(this.selectors, this);

      if (MinimogTheme.config.mqlMobile && this.mobileDisableSlider) {
        slideControls && slideControls.classList.add("m:hidden");
        slideContainer && slideContainer.classList.remove("swiper-container");
        if (this.swiper && !this.swiper.destroyed) this.swiper.destroy(true, true);
        this.swiper = this.slider = null;
        if (slideContainer) {
          slideContainer.querySelectorAll(".swiper-slide").forEach(function (slide) {
            slide.style.width = "";
            slide.style.marginRight = "";
          });
          var wrap = slideContainer.querySelector(".swiper-wrapper");
          if (wrap) wrap.style.transform = "";
        }
      } else {
        this.initSlider();
      }
    }

    initSlider() {
      const { slideContainer } = queryDomNodes(this.selectors, this);
      const controlsContainer = this.querySelector(".m-slider-controls");
      const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
      if (!slideContainer || typeof MinimogLibs?.Swiper !== "function") return;
      // Breakpoint flips call this again; a second Swiper on the same container plus another
      // pair of arrow listeners made one arrow click move several slides.
      if (this.slider && !this.slider.destroyed && this.slider.el === slideContainer) {
        this.slider.update();
        this.syncSliderControls(this.slider);
        return;
      }

      slideContainer.classList.add("swiper-container");

      this.slider = new MinimogLibs.Swiper(slideContainer, {
        slidesPerView: 2,
        showPagination: this.showPagination,
        showNavigation: this.showNavigation,
        loop: false,
        watchOverflow: true,
        navigation: {
          prevEl: this.showNavigation ? prevButton : null,
          nextEl: this.showNavigation ? nextButton : null,
        },
        pagination: this.showPagination
          ? {
            el: this.querySelector(".swiper-pagination"),
            clickable: true,
          }
          : false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.items) >= 3 ? 3 : parseInt(this.items),
          },
          1024: {
            slidesPerView: parseInt(this.items) >= 4 ? 4 : parseInt(this.items),
          },
          1280: {
            slidesPerView: parseInt(this.items),
          },
        },
        threshold: 2,
        on: {
          init: swiper => {
            this.syncSliderControls(swiper);
            setTimeout(() => this.syncSliderControls(swiper), 200);
          },
          resize: swiper => this.syncSliderControls(swiper),
          lock: swiper => this.syncSliderControls(swiper),
          unlock: swiper => this.syncSliderControls(swiper),
        },
      });

      this.swiper = this.slider;
    }

    syncSliderControls(swiper) {
      if (!swiper || swiper.destroyed) return;
      const { slideControls } = queryDomNodes(this.selectors, this);
      if (!slideControls) return;
      slideControls.classList.toggle("m:hidden", swiper.isLocked);
      const firstItem = this.querySelector(".m-image") || this.querySelector(".m-placeholder-svg");
      if (firstItem) slideControls.style.setProperty("--offset-top", firstItem.clientHeight / 2 + "px");
      slideControls.querySelectorAll(".m-slider-controls__button").forEach(button => button.classList.remove("m:hidden"));
    }

    getQueryString() {
      const cookieName = "minimog-recently-viewed";
      let items = [];
      try {
        const saved = JSON.parse(window.localStorage.getItem(cookieName) || "[]");
        if (Array.isArray(saved)) items = saved.filter(id => Number.isSafeInteger(id) && id > 0);
      } catch (_) { return ''; }
      if (this.dataset.productId && items.includes(parseInt(this.dataset.productId))) {
        items.splice(items.indexOf(parseInt(this.dataset.productId)), 1);
      }
      return items
        .map((item) => "id:" + item)
        .slice(0, this.productsToshow)
        .join(" OR ");
    }
  }
  customElements.define("m-recently-viewed", MRecentlyViewed);
}
